import React, { useEffect, useState } from 'react';
import addDays from 'date-fns/addDays';
import isBefore from 'date-fns/isBefore';
import startOfToday from 'date-fns/startOfToday';
import { Subscribe } from 'unstated';
import OriginTracing from '@atlassiansox/origin-tracing';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { BannerStateContainer } from '@confluence/banners';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { AccessStatus, ConfluenceEdition, useSessionData } from '@confluence/session-data';
import { confluenceLocalStorageInstance as localStorage, keys } from '@confluence/storage-manager';
import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';

import { useTrialDetails } from '../hooks/trial-details';
import { usePaymentDetails } from '../hooks/payment-details';

import { EotBanner } from './EotBanner';

const BANNER_NAME = 'EotBanner';
const EXPERIMENT = 'confluence_eot_banner_experiment';

/**
 * Adds the `atlOrigin` param to the url
 */
function addAtlassianOriginToUrl(url: string): string {
	const origin = new OriginTracing({ product: 'confluence' });
	return origin.addToUrl(url);
}

/**
 * Responsible for checking eligibility and rendering the `EotBanner` with the subscription to the `BannerStateContainer`.
 */
export const EotBannerContainer = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [canFireExposureEvents, setCanFireExposureEvents] = useState(true);
	const [isDismissed, setDismissed] = useState(() => {
		// Has this banner already been dismissed?
		const dismissalItem = localStorage.getItem(keys.EOT_BANNER_DISMISS_UNTIL);
		return !!dismissalItem && isBefore(startOfToday(), Date.parse(dismissalItem));
	});

	// It's cheap to get info from the session. So let's get that first.
	const { accessStatus, userId, edition } = useSessionData();

	// We're not restricting to admins at this point. That check will happen further down with `canPay`.
	const isUserEligible =
		userId &&
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS);

	// Both standard and premium
	const isEditionEligible =
		edition === ConfluenceEdition.PREMIUM || edition === ConfluenceEdition.STANDARD;

	// Query TCS on the Confluence backend - second cheapest.
	const { isTrialDataAvailable, billingSourceSystem, daysLeftInTrial } = useTrialDetails(
		!isUserEligible || !isEditionEligible || isDismissed,
	);

	const isEotBannerEligible =
		daysLeftInTrial >= 1 &&
		daysLeftInTrial <= 5 &&
		billingSourceSystem === 'CCP' &&
		isUserEligible &&
		isEditionEligible;

	// We can't fire exposure just yet; we haven't finished the eligibility checks.
	const [confluenceEotBannerExperimentConfig, fireConfluenceEotBannerExperimentExposure] =
		UNSAFE_noExposureExp('confluence_eot_banner_experiment');
	const cohort = confluenceEotBannerExperimentConfig.get('cohort', 'not-enrolled');

	// Used to determine whether to skip the more expensive AGG call.
	const isEnrolledInExperiment = cohort !== 'not-enrolled';

	// AGG call
	const { isPaymentDataAvailable, isPaymentOnFile, paymentFlowUrl, canUserPay } = usePaymentDetails(
		!isTrialDataAvailable || !isEnrolledInExperiment || isDismissed,
	);

	// Now that we have payment data, are we still eligible?
	const isPaymentDataEligibleForExperiment =
		isPaymentDataAvailable &&
		!isPaymentOnFile &&
		!!paymentFlowUrl &&
		paymentFlowUrl !== '' &&
		canUserPay;

	// The final eligibility check
	const isEligible =
		isEotBannerEligible &&
		isPaymentDataEligibleForExperiment &&
		isEnrolledInExperiment &&
		!isDismissed;

	useEffect(() => {
		if (canFireExposureEvents && isEligible) {
			fireConfluenceEotBannerExperimentExposure();

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'experiment',
					actionSubjectId: `${EXPERIMENT}-exposed`,
					attributes: {
						cohort,
					},
					source: EXPERIMENT,
				},
			}).fire();

			setCanFireExposureEvents(false);
		}
	}, [
		canFireExposureEvents,
		cohort,
		createAnalyticsEvent,
		isEligible,
		fireConfluenceEotBannerExperimentExposure,
	]);

	if (!isEligible || cohort === 'control') {
		return null;
	}

	return (
		<Subscribe to={[BannerStateContainer]}>
			{(bannerState: BannerStateContainer) => {
				bannerState?.show(BANNER_NAME, 48);

				return (
					<EotBanner
						edition={edition}
						paymentFlowUrl={addAtlassianOriginToUrl(paymentFlowUrl)}
						daysLeftInTrial={daysLeftInTrial}
						onDismiss={() => {
							// We want to put an expiry on the dismissal so that the dismissal does not apply to the next trial.
							const dismissUntilDate = addDays(startOfToday(), daysLeftInTrial + 1);
							localStorage.setItem(keys.EOT_BANNER_DISMISS_UNTIL, dismissUntilDate.toISOString());
							// The actual dismissal
							setDismissed(true);
							bannerState?.hide(BANNER_NAME);
						}}
					/>
				);
			}}
		</Subscribe>
	);
};

/**
 * Wraps an error boundary around `EotBannerContainer`.
 */
export const EotBannerContainerWithErrorBoundary = withErrorBoundary({
	attribution: Attribution.GROWTH_LUNA,
})(EotBannerContainer);
